import { PDFDocument, PDFPage, StandardFonts, rgb } from 'pdf-lib';

const LETTER_PAGE_WIDTH = 612;
const LETTER_PAGE_HEIGHT = 792;
const LEFT_MARGIN = 56;
const TOP_MARGIN = 56;
const BOTTOM_MARGIN = 56;
const BODY_FONT_SIZE = 12;
const TITLE_FONT_SIZE = 20;
const LINE_HEIGHT = 18;
const MAX_BODY_CHARS_PER_LINE = 88;
const PDF_BLOB_REVOKE_DELAY_MS = 60_000;
const PDF_FALLBACK_SHELL_BLOB_REVOKE_DELAY_MS = 10 * 60_000;

export type PdfOpenMode = 'preview' | 'download';

const installPdfFallbackShell = (targetWindow: Window, url: string, fileName: string): boolean => {
  const doc = targetWindow.document;
  const body = doc.body;
  if (!body) return false;
  doc.documentElement.style.background = '#ffffff';
  body.style.margin = '0';
  body.style.background = '#ffffff';
  body.replaceChildren();
  const shell = doc.createElement('main');
  shell.style.cssText = [
    'font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    'box-sizing: border-box',
    'min-height: 100vh',
    'padding: 32px',
    'line-height: 1.5',
    'color: #243b3a',
    'background: #ffffff',
  ].join(';');
  const heading = doc.createElement('h1');
  heading.textContent = 'Your PDF is ready';
  heading.style.cssText = 'font-size: 24px; margin: 0 0 12px;';
  const message = doc.createElement('p');
  message.textContent = 'If the preview below stays blank, use Preview PDF or Download PDF.';
  message.style.cssText = 'margin: 0 0 20px; max-width: 720px;';
  const actions = doc.createElement('nav');
  actions.style.cssText = 'display: flex; flex-wrap: wrap; gap: 12px; margin: 0 0 20px;';
  const openLink = doc.createElement('a');
  openLink.href = url;
  openLink.target = '_self';
  openLink.dataset.testid = 'generated-pdf-fallback-link';
  openLink.textContent = 'Preview PDF';
  openLink.style.cssText = [
    'display: inline-block',
    'border-radius: 8px',
    'background: #11645f',
    'color: white',
    'font-weight: 700',
    'padding: 12px 18px',
    'text-decoration: none',
  ].join(';');
  const downloadLink = doc.createElement('a');
  downloadLink.href = url;
  downloadLink.download = fileName;
  downloadLink.dataset.testid = 'generated-pdf-download-link';
  downloadLink.textContent = 'Download PDF';
  downloadLink.style.cssText = [
    'display: inline-block',
    'border: 1px solid #b7cdcb',
    'border-radius: 8px',
    'background: #ffffff',
    'color: #11645f',
    'font-weight: 700',
    'padding: 12px 18px',
    'text-decoration: none',
  ].join(';');
  const frame = doc.createElement('iframe');
  frame.src = url;
  frame.dataset.testid = 'generated-pdf-frame';
  frame.title = fileName;
  frame.style.cssText = [
    'display: block',
    'width: 100%',
    'height: calc(100vh - 180px)',
    'min-height: 480px',
    'border: 1px solid #d5e0df',
    'border-radius: 8px',
    'background: #ffffff',
  ].join(';');
  actions.append(openLink, downloadLink);
  shell.append(heading, message, actions, frame);
  body.append(shell);
  try {
    targetWindow.opener = null;
  } catch {
    // Ignore browser-managed opener lifecycle errors.
  }
  return true;
};

const installPdfFallbackError = (targetWindow: Window): void => {
  const doc = targetWindow.document;
  const body = doc.body;
  if (!body) return;
  body.replaceChildren();
  const shell = doc.createElement('main');
  shell.style.cssText = [
    'font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    'margin: 48px auto',
    'max-width: 520px',
    'line-height: 1.5',
    'color: #7f1d1d',
  ].join(';');
  const heading = doc.createElement('h1');
  heading.textContent = 'We could not open this PDF';
  heading.style.cssText = 'font-size: 24px; margin: 0 0 12px;';
  const message = doc.createElement('p');
  message.textContent = 'Please close this tab, allow pop-ups, and try again.';
  message.style.cssText = 'margin: 0;';
  shell.append(heading, message);
  body.append(shell);
};

const triggerPdfBlobDownload = (url: string, fileName: string): boolean => {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener noreferrer';
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return true;
};

const escapePdfShellHtml = (value: string): string => value.replace(/[&<>"']/g, (character) => {
  switch (character) {
    case '&':
      return '&amp;';
    case '<':
      return '&lt;';
    case '>':
      return '&gt;';
    case '"':
      return '&quot;';
    default:
      return '&#39;';
  }
});

const buildPdfFallbackShellHtml = (url: string, fileName: string): string => {
  const safeUrl = escapePdfShellHtml(url);
  const safeFileName = escapePdfShellHtml(fileName);
  return [
    '<!doctype html>',
    '<html lang="en">',
    '<head>',
    '  <meta charset="utf-8">',
    '  <meta name="viewport" content="width=device-width, initial-scale=1">',
    '  <title>' + safeFileName + '</title>',
    '  <style>',
    '    html, body { margin: 0; min-height: 100%; background: #ffffff; color: #243b3a; }',
    '    body { font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }',
    '    main { box-sizing: border-box; min-height: 100vh; padding: 32px; line-height: 1.5; background: #ffffff; }',
    '    h1 { font-size: 24px; margin: 0 0 12px; }',
    '    p { margin: 0 0 20px; max-width: 720px; }',
    '    nav { display: flex; flex-wrap: wrap; gap: 12px; margin: 0 0 20px; }',
    '    a { display: inline-block; border-radius: 8px; font-weight: 700; padding: 12px 18px; text-decoration: none; }',
    '    a[data-testid="generated-pdf-fallback-link"] { background: #11645f; color: #ffffff; }',
    '    a[data-testid="generated-pdf-download-link"] { border: 1px solid #b7cdcb; background: #ffffff; color: #11645f; }',
    '    iframe { display: block; width: 100%; height: calc(100vh - 180px); min-height: 480px; border: 1px solid #d5e0df; border-radius: 8px; background: #ffffff; }',
    '  </style>',
    '</head>',
    '<body>',
    '  <main>',
    '    <h1>Your PDF is ready</h1>',
    '    <p>If the preview below stays blank, use Preview PDF or Download PDF.</p>',
    '    <nav>',
    '      <a data-testid="generated-pdf-fallback-link" href="' + safeUrl + '" target="_self">Preview PDF</a>',
    '      <a data-testid="generated-pdf-download-link" href="' + safeUrl + '" download="' + safeFileName + '">Download PDF</a>',
    '    </nav>',
    '    <iframe data-testid="generated-pdf-frame" src="' + safeUrl + '" title="' + safeFileName + '"></iframe>',
    '  </main>',
    '</body>',
    '</html>',
  ].join('\n');
};

export const openPdfUrlInFallbackShell = ({
  url,
  fileName,
  targetWindow = null,
}: {
  url: string;
  fileName: string;
  targetWindow?: Window | null;
}): boolean => {
  if (targetWindow && !targetWindow.closed) {
    return installPdfFallbackShell(targetWindow, url, fileName);
  }
  if (typeof window === 'undefined' || typeof window.open !== 'function') {
    return false;
  }
  const shellUrl = `data:text/html;charset=utf-8,${encodeURIComponent(buildPdfFallbackShellHtml(url, fileName))}`;
  const popup = window.open(shellUrl, '_blank', 'noopener,noreferrer');
  return Boolean(popup);
};

const wrapLine = (line: string, limit = MAX_BODY_CHARS_PER_LINE): string[] => {
  const trimmed = line.trimEnd();
  if (!trimmed) {
    return [''];
  }
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [''];
  }
  const rows: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= limit) {
      current = next;
      continue;
    }
    if (current) {
      rows.push(current);
    }
    current = word;
  }
  if (current) {
    rows.push(current);
  }
  return rows.length > 0 ? rows : [''];
};

export type OpenTextPdfInNewTabInput = {
  title: string;
  lines: string[];
  fileName?: string;
};

export type SectionedPdfRow = {
  label: string;
  value: string;
};

export type SectionedPdfSection = {
  title: string;
  rows: SectionedPdfRow[];
};

export type OpenSectionedPdfInNewTabInput = {
  title: string;
  subtitle?: string;
  sections: SectionedPdfSection[];
  fileName?: string;
  targetWindow?: Window | null;
  openMode?: PdfOpenMode;
};

export type TuitionFamiliesReportSummaryItem = {
  label: string;
  value: string;
};

export type TuitionFamiliesReportRow = {
  familyName: string;
  students: string;
  annualAmount: string;
  paid: string;
  balance: string;
  plan: string;
  status: string;
  autoPay: string;
  dueDate: string;
};

export type OpenTuitionFamiliesReportPdfInNewTabInput = {
  academicYear: string;
  generatedOnLabel: string;
  familyCount: number;
  summaryItems: TuitionFamiliesReportSummaryItem[];
  rows: TuitionFamiliesReportRow[];
  fileName?: string;
};

const openPdfBlobInNewTab = (
  bytes: Uint8Array,
  fileName: string,
  targetWindow?: Window | null,
  openMode: PdfOpenMode = 'preview',
): boolean => {
  const normalizedBytes = new Uint8Array(bytes.length);
  normalizedBytes.set(bytes);
  const blob = new Blob([normalizedBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  if (openMode === 'download') {
    const downloaded = triggerPdfBlobDownload(url, fileName);
    window.setTimeout(() => URL.revokeObjectURL(url), PDF_BLOB_REVOKE_DELAY_MS);
    return downloaded;
  }
  if (targetWindow) {
    let installedShell = false;
    try {
      targetWindow.document.title = fileName;
      targetWindow.opener = null;
      installedShell = installPdfFallbackShell(targetWindow, url, fileName);
    } catch {
      // Browser PDF viewers can restrict access to the placeholder document.
    }
    if (installedShell) {
      window.setTimeout(() => URL.revokeObjectURL(url), PDF_FALLBACK_SHELL_BLOB_REVOKE_DELAY_MS);
      return true;
    }
    try {
      targetWindow.location.href = url;
    } catch {
      try {
        targetWindow.location.assign(url);
      } catch {
        try {
          installPdfFallbackError(targetWindow);
        } catch {
          // The parent page still receives the false return and shows the recoverable error.
        }
        URL.revokeObjectURL(url);
        return false;
      }
    }
    window.setTimeout(() => URL.revokeObjectURL(url), PDF_FALLBACK_SHELL_BLOB_REVOKE_DELAY_MS);
    return true;
  }
  const shellUrl = URL.createObjectURL(new Blob([buildPdfFallbackShellHtml(url, fileName)], { type: 'text/html' }));
  const opened = window.open(shellUrl, '_blank');
  if (!opened) {
    const link = document.createElement('a');
    link.href = shellUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => {
      URL.revokeObjectURL(shellUrl);
      URL.revokeObjectURL(url);
    }, PDF_FALLBACK_SHELL_BLOB_REVOKE_DELAY_MS);
    return true;
  }
  try {
    opened.opener = null;
  } catch {
    // Cross-browser generated blob documents may not expose the opened shell.
  }
  window.setTimeout(() => {
    URL.revokeObjectURL(shellUrl);
    URL.revokeObjectURL(url);
  }, PDF_FALLBACK_SHELL_BLOB_REVOKE_DELAY_MS);
  return true;
};

const drawCellText = (
  page: PDFPage,
  text: string,
  x: number,
  y: number,
  options: {
    font: Awaited<ReturnType<PDFDocument['embedFont']>>;
    size: number;
    color?: ReturnType<typeof rgb>;
  },
) => {
  page.drawText(text, {
    x,
    y,
    size: options.size,
    font: options.font,
    color: options.color ?? rgb(0.11, 0.15, 0.2),
  });
};

const resolveStatusColor = (status: string) => {
  const normalized = status.trim().toLowerCase();
  if (normalized === 'current') {
    return rgb(0.19, 0.84, 0.62);
  }
  if (normalized === 'paid in full') {
    return rgb(0.15, 0.59, 0.64);
  }
  if (normalized === 'overdue') {
    return rgb(0.98, 0.17, 0.41);
  }
  if (normalized === 'pending') {
    return rgb(1, 0.56, 0.47);
  }
  if (normalized === 'archived') {
    return rgb(0.62, 0.65, 0.69);
  }
  return rgb(0.36, 0.42, 0.49);
};

const resolveDueDateColor = (dueDate: string) => {
  const normalized = dueDate.trim().toLowerCase();
  if (normalized.includes('overdue') || normalized.includes('past due')) {
    return rgb(0.98, 0.17, 0.41);
  }
  if (normalized.includes('due today')) {
    return rgb(1, 0.56, 0.47);
  }
  if (normalized.includes('due in')) {
    return rgb(0.19, 0.5, 0.4);
  }
  return rgb(0.48, 0.53, 0.58);
};

export const openTextPdfInNewTab = async ({
  title,
  lines,
  fileName = 'document.pdf',
}: OpenTextPdfInNewTabInput): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }

  const pdf = await PDFDocument.create();
  const page = pdf.addPage([LETTER_PAGE_WIDTH, LETTER_PAGE_HEIGHT]);
  const headingFont = await pdf.embedFont(StandardFonts.HelveticaBold);
  const bodyFont = await pdf.embedFont(StandardFonts.Helvetica);

  let y = LETTER_PAGE_HEIGHT - TOP_MARGIN;
  page.drawText(title, {
    x: LEFT_MARGIN,
    y,
    size: TITLE_FONT_SIZE,
    font: headingFont,
    color: rgb(0.11, 0.23, 0.24),
  });

  y -= TITLE_FONT_SIZE + 14;
  for (const originalLine of lines) {
    const wrappedLines = wrapLine(String(originalLine ?? ''));
    for (const line of wrappedLines) {
      if (y <= BOTTOM_MARGIN) {
        break;
      }
      page.drawText(line, {
        x: LEFT_MARGIN,
        y,
        size: BODY_FONT_SIZE,
        font: bodyFont,
        color: rgb(0.11, 0.15, 0.2),
      });
      y -= LINE_HEIGHT;
    }
    if (y <= BOTTOM_MARGIN) {
      break;
    }
  }

  const bytes = await pdf.save();
  return openPdfBlobInNewTab(bytes, fileName);
};

export const openSectionedPdfInNewTab = async ({
  title,
  subtitle,
  sections,
  fileName = 'document.pdf',
  targetWindow,
  openMode = 'preview',
}: OpenSectionedPdfInNewTabInput): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }

  const pdf = await PDFDocument.create();
  const headingFont = await pdf.embedFont(StandardFonts.HelveticaBold);
  const bodyFont = await pdf.embedFont(StandardFonts.Helvetica);
  const mutedColor = rgb(0.43, 0.49, 0.5);
  const textColor = rgb(0.11, 0.15, 0.2);
  const darkTeal = rgb(0.06, 0.25, 0.25);
  const borderColor = rgb(0.83, 0.88, 0.89);
  const headerFill = rgb(0.94, 0.97, 0.97);
  let page = pdf.addPage([LETTER_PAGE_WIDTH, LETTER_PAGE_HEIGHT]);
  let y = LETTER_PAGE_HEIGHT - 48;
  const contentWidth = LETTER_PAGE_WIDTH - 96;
  const left = 48;

  const addPage = () => {
    page = pdf.addPage([LETTER_PAGE_WIDTH, LETTER_PAGE_HEIGHT]);
    y = LETTER_PAGE_HEIGHT - 48;
  };

  const ensureSpace = (height: number) => {
    if (y - height < BOTTOM_MARGIN) {
      addPage();
    }
  };

  page.drawText(title, {
    x: left,
    y,
    size: 20,
    font: headingFont,
    color: darkTeal,
  });
  y -= 18;
  if (subtitle) {
    page.drawText(subtitle, {
      x: left,
      y,
      size: 9,
      font: bodyFont,
      color: mutedColor,
    });
    y -= 24;
  } else {
    y -= 18;
  }

  for (const section of sections) {
    const rows = section.rows.filter((row) => row.value.trim());
    if (rows.length === 0) continue;
    ensureSpace(34);
    page.drawRectangle({
      x: left,
      y: y - 18,
      width: contentWidth,
      height: 20,
      color: darkTeal,
    });
    page.drawText(section.title.toUpperCase(), {
      x: left + 10,
      y: y - 12,
      size: 8,
      font: headingFont,
      color: rgb(1, 1, 1),
    });
    y -= 26;

    for (const row of rows) {
      const labelLines = wrapLine(row.label, 28);
      const valueLines = wrapLine(row.value, 58);
      const lineCount = Math.max(labelLines.length, valueLines.length);
      const rowHeight = Math.max(24, lineCount * 12 + 10);
      ensureSpace(rowHeight + 2);
      page.drawRectangle({
        x: left,
        y: y - rowHeight + 4,
        width: contentWidth,
        height: rowHeight,
        color: headerFill,
        borderColor,
        borderWidth: 0.5,
      });
      labelLines.forEach((line, index) => {
        page.drawText(line, {
          x: left + 10,
          y: y - 10 - index * 12,
          size: 7.5,
          font: headingFont,
          color: mutedColor,
        });
      });
      valueLines.forEach((line, index) => {
        page.drawText(line, {
          x: left + 180,
          y: y - 10 - index * 12,
          size: 8.5,
          font: bodyFont,
          color: textColor,
        });
      });
      y -= rowHeight;
    }
    y -= 12;
  }

  const bytes = await pdf.save();
  return openPdfBlobInNewTab(bytes, fileName, targetWindow, openMode);
};

export const openTuitionFamiliesReportPdfInNewTab = async ({
  academicYear,
  generatedOnLabel,
  familyCount,
  summaryItems,
  rows,
  fileName = 'tuition-families.pdf',
}: OpenTuitionFamiliesReportPdfInNewTabInput): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }

  const pdf = await PDFDocument.create();
  const page = pdf.addPage([LETTER_PAGE_WIDTH, LETTER_PAGE_HEIGHT]);
  const headingFont = await pdf.embedFont(StandardFonts.HelveticaBold);
  const bodyFont = await pdf.embedFont(StandardFonts.Helvetica);

  const title = 'Tuition - Families Report';
  let y = LETTER_PAGE_HEIGHT - 44;

  page.drawText(title, {
    x: 28,
    y,
    size: 16,
    font: headingFont,
    color: rgb(0.23, 0.3, 0.3),
  });

  y -= 18;
  page.drawText(
    `Academic year ${academicYear}  •  Generated ${generatedOnLabel}  •  ${familyCount} families`,
    {
      x: 28,
      y,
      size: 7,
      font: bodyFont,
      color: rgb(0.48, 0.53, 0.58),
    },
  );

  y -= 22;
  const summaryCardX = 28;
  const summaryCardWidth = LETTER_PAGE_WIDTH - 56;
  const summaryCardHeight = 24;
  page.drawRectangle({
    x: summaryCardX,
    y: y - summaryCardHeight + 2,
    width: summaryCardWidth,
    height: summaryCardHeight,
    color: rgb(0.96, 0.96, 0.95),
    borderColor: rgb(0.9, 0.91, 0.91),
    borderWidth: 0.5,
  });

  const summaryColumnWidth = summaryCardWidth / Math.max(summaryItems.length, 1);
  summaryItems.slice(0, 4).forEach((item, index) => {
    drawCellText(page, `${item.label}: ${item.value}`, summaryCardX + 12 + summaryColumnWidth * index, y - 8, {
      font: headingFont,
      size: 6.5,
      color: rgb(0.28, 0.32, 0.34),
    });
  });

  y -= 36;
  const tableX = 28;
  const tableWidth = LETTER_PAGE_WIDTH - 56;
  const rowHeight = 17;
  const headerHeight = 18;
  const columns = [
    { key: 'familyName', label: 'FAMILY NAME', width: 112 },
    { key: 'students', label: 'STUDENTS', width: 48 },
    { key: 'annualAmount', label: 'ANNUAL AMOUNT', width: 80 },
    { key: 'paid', label: 'PAID', width: 62 },
    { key: 'balance', label: 'BALANCE', width: 76 },
    { key: 'plan', label: 'PLAN', width: 66 },
    { key: 'status', label: 'STATUS', width: 74 },
    { key: 'autoPay', label: 'AUTO-PAY', width: 46 },
    { key: 'dueDate', label: 'DUE DATE', width: 48 },
  ] as const;

  page.drawRectangle({
    x: tableX,
    y: y - headerHeight + 2,
    width: tableWidth,
    height: headerHeight,
    color: rgb(0.96, 0.96, 0.95),
  });

  let columnX = tableX + 2;
  columns.forEach((column) => {
    drawCellText(page, column.label, columnX + 4, y - 11, {
      font: headingFont,
      size: 6,
      color: rgb(0.48, 0.53, 0.58),
    });
    columnX += column.width;
  });

  y -= headerHeight + 2;
  rows.slice(0, 22).forEach((row, rowIndex) => {
    const baselineY = y - rowHeight * rowIndex;
    page.drawLine({
      start: { x: tableX, y: baselineY - 1 },
      end: { x: tableX + tableWidth, y: baselineY - 1 },
      thickness: 0.5,
      color: rgb(0.89, 0.91, 0.93),
    });

    let x = tableX + 2;
    drawCellText(page, row.familyName, x + 4, baselineY - 11, {
      font: headingFont,
      size: 6.7,
      color: rgb(0.16, 0.62, 0.71),
    });
    x += columns[0].width;

    drawCellText(page, row.students, x + 18, baselineY - 11, {
      font: bodyFont,
      size: 6.5,
      color: rgb(0.36, 0.42, 0.49),
    });
    x += columns[1].width;

    drawCellText(page, row.annualAmount, x + 4, baselineY - 11, {
      font: bodyFont,
      size: 6.5,
      color: rgb(0.36, 0.42, 0.49),
    });
    x += columns[2].width;

    drawCellText(page, row.paid, x + 4, baselineY - 11, {
      font: bodyFont,
      size: 6.5,
      color: rgb(0.36, 0.42, 0.49),
    });
    x += columns[3].width;

    drawCellText(page, row.balance, x + 4, baselineY - 11, {
      font: headingFont,
      size: 6.2,
      color: rgb(0.36, 0.42, 0.49),
    });
    x += columns[4].width;

    drawCellText(page, row.plan, x + 4, baselineY - 11, {
      font: bodyFont,
      size: 6,
      color: rgb(0.36, 0.42, 0.49),
    });
    x += columns[5].width;

    const statusColor = resolveStatusColor(row.status);
    page.drawCircle({
      x: x + 7,
      y: baselineY - 8,
      size: 3.2,
      color: statusColor,
    });
    drawCellText(page, row.status, x + 14, baselineY - 11, {
      font: bodyFont,
      size: 6.5,
      color: statusColor,
    });
    x += columns[6].width;

    drawCellText(page, row.autoPay, x + 8, baselineY - 11, {
      font: headingFont,
      size: 6.5,
      color: row.autoPay === 'Yes' ? rgb(0.19, 0.84, 0.62) : rgb(0.36, 0.42, 0.49),
    });
    x += columns[7].width;

    drawCellText(page, row.dueDate, x + 4, baselineY - 11, {
      font: bodyFont,
      size: 6.3,
      color: resolveDueDateColor(row.dueDate),
    });
  });

  page.drawText('Intellio — Tuition Management', {
    x: 28,
    y: 18,
    size: 6,
    font: bodyFont,
    color: rgb(0.65, 0.68, 0.72),
  });
  page.drawText('Page 1 of 1', {
    x: LETTER_PAGE_WIDTH - 62,
    y: 18,
    size: 6,
    font: bodyFont,
    color: rgb(0.65, 0.68, 0.72),
  });

  const bytes = await pdf.save();
  return openPdfBlobInNewTab(bytes, fileName);
};
